import {
	MutableRefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'

import { UiState } from 'instantsearch.js'
import debounce from 'lodash/debounce'
import useAiSearchStore from '@/stores/aiSearchStore'
import { useInstantSearch } from 'react-instantsearch'
import { useRouter } from 'next/router'

interface AISearchState {
	searchByAudio: string
	searchByPrompt: string
}

type AlgoliaStateParams = {
	menu?: Record<string, string | null>
	query?: string
	range?: Record<string, string | null>
	refinementList?: Record<string, string[] | null>
	sortBy?: string | null
	toggle?: Record<string, boolean | null>
	page?: number
}

type InstantSearchQueryParams = {
	[K in string as `${K}[${keyof AlgoliaStateParams}]`]?: string | string[]
}

interface RouterQuery extends Record<string, string | string[]> {
	searchByAudio?: string
	searchByPrompt?: string
}

type SearchRouterQuery = RouterQuery & InstantSearchQueryParams

type UsePersistSearchScrollPositionProps = {
	clearSearchScrollPosition: () => void
	containerRef: MutableRefObject<HTMLDivElement | null>
}

// Handle URL updates with debouncing
const useUrlUpdater = (): ((
	action: 'push' | 'replace',
	query: SearchRouterQuery
) => void) => {
	const router = useRouter()
	const debouncedUpdateRef = useRef<ReturnType<typeof debounce>>()

	const { clearAiSearch } = useAiSearchStore()
	const { setIndexUiState } = useInstantSearch()

	const debouncedRouterUpdate = debounce(
		(action: 'push' | 'replace', query: SearchRouterQuery) => {
			const currentQuery = router.query
			if (JSON.stringify(currentQuery) === JSON.stringify(query)) return

			router[action]({ pathname: router.pathname, query }, undefined, {
				shallow: true,
			})
		},
		300
	)

	debouncedUpdateRef.current = debouncedRouterUpdate

	useEffect(() => {
		const resetSearch = (): void => {
			sessionStorage.removeItem('activeSearchScrollPosition')
			sessionStorage.removeItem('activeSearchTrackRow')
			clearAiSearch()
			setIndexUiState((prevState) => ({
				...prevState,
				menu: null,
				query: '',
				range: null,
				refinementList: null,
				sortBy: null,
				toggle: null,
			}))

			const mainContent = document.getElementById('main-content')

			mainContent.scrollTo(0, 0)
		}

		const searchLinks = document.querySelectorAll(
			'a[href^="/track/searchkeyword"]'
		)
		searchLinks.forEach((link) => {
			link.addEventListener('click', resetSearch)
		})

		return () => {
			debouncedUpdateRef.current?.cancel()
			searchLinks.forEach((link) => {
				link.removeEventListener('click', resetSearch)
			})
		}
	}, [clearAiSearch, setIndexUiState])

	return useCallback(
		(action: 'push' | 'replace', query: SearchRouterQuery) => {
			debouncedRouterUpdate(action, query)
		},
		[debouncedRouterUpdate]
	)
}

// Handle initial route state
const useInitialRoute = (
	isLoggedIn: boolean
): {
	isInitialRender: React.MutableRefObject<boolean>
	lastKnownState: React.MutableRefObject<AISearchState>
} => {
	const isInitialRender = useRef(true)
	const router = useRouter()
	const lastKnownState = useRef<AISearchState>({
		searchByAudio: '',
		searchByPrompt: '',
	})
	const { prefetchSearchByAudio, setSearchByAudio, setSearchByPrompt } =
		useAiSearchStore()

	useEffect(() => {
		if (!isInitialRender.current) return

		if (router.query?.searchByAudio) {
			const audioLink = router.query.searchByAudio as string
			prefetchSearchByAudio(audioLink, isLoggedIn)
			setSearchByAudio(audioLink)
			lastKnownState.current.searchByAudio = audioLink
		}
		// Only process URL parameters if user is logged in
		if (isLoggedIn) {
			if (router.query?.searchByPrompt) {
				const prompt = router.query.searchByPrompt as string
				setSearchByPrompt(prompt)
				lastKnownState.current.searchByPrompt = prompt
			}
		}

		isInitialRender.current = false
	}, [
		router.query,
		isLoggedIn,
		prefetchSearchByAudio,
		setSearchByAudio,
		setSearchByPrompt,
	])

	return { isInitialRender, lastKnownState }
}

// Handle InstantSearch state updates
const syncInstantSearchState = (
	trackIndex: string | undefined,
	uiState: UiState,
	query: SearchRouterQuery
): SearchRouterQuery => {
	const newQuery = { ...query }

	if (trackIndex) {
		if (uiState[trackIndex]?.query) {
			newQuery[`${trackIndex}[query]`] = uiState[trackIndex].query
		}
		if (uiState[trackIndex]?.page) {
			newQuery[`${trackIndex}[page]`] = String(uiState[trackIndex].page)
		}
	}

	return newQuery
}

export const useAISearchRouter = (
	isLoggedIn: boolean,
	uiState: UiState,
	trackIndex?: string
): void => {
	const router = useRouter()
	const updateUrl = useUrlUpdater()
	const { isInitialRender, lastKnownState } = useInitialRoute(isLoggedIn)

	const {
		clearAiSearch,
		searchByAudio,
		searchByPrompt,
		setSearchByAudio,
		setSearchByPrompt,
	} = useAiSearchStore()

	// Handle URL updates based on search state changes
	useEffect(() => {
		if (isInitialRender.current) return

		const newQuery = { ...router.query } as SearchRouterQuery

		// Update audio search state
		if (!searchByAudio?.url && newQuery.searchByAudio) {
			delete newQuery.searchByAudio
			lastKnownState.current.searchByAudio = ''
		} else if (searchByAudio?.url) {
			newQuery.searchByAudio = searchByAudio.url
			lastKnownState.current.searchByAudio = searchByAudio.url
		}

		// Update prompt search state only if user is logged in
		if (isLoggedIn) {
			if (!searchByPrompt && newQuery.searchByPrompt) {
				delete newQuery.searchByPrompt
				lastKnownState.current.searchByPrompt = ''
			} else if (searchByPrompt) {
				newQuery.searchByPrompt = searchByPrompt
				lastKnownState.current.searchByPrompt = searchByPrompt
			}
		}

		const queryWithInstantSearch = syncInstantSearchState(
			trackIndex,
			uiState,
			newQuery
		)

		updateUrl('replace', queryWithInstantSearch)
	}, [
		uiState,
		router.query,
		updateUrl,
		searchByAudio,
		searchByPrompt,
		trackIndex,
		isInitialRender,
		lastKnownState,
		isLoggedIn,
	])

	// Handle browser back/forward navigation
	useEffect(() => {
		const handlePopState = (): void => {
			const audioParam = router.query?.searchByAudio as string
			const promptParam = router.query?.searchByPrompt as string

			// Handle audio search changes
			if (!audioParam && lastKnownState.current.searchByAudio) {
				clearAiSearch()
				lastKnownState.current.searchByAudio = ''
			} else if (audioParam) {
				setSearchByAudio(audioParam)
				lastKnownState.current.searchByAudio = audioParam
			}

			// Handle prompt search changes
			if (!promptParam && lastKnownState.current.searchByPrompt) {
				setSearchByPrompt('')
				lastKnownState.current.searchByPrompt = ''
			} else if (promptParam) {
				setSearchByPrompt(promptParam)
				lastKnownState.current.searchByPrompt = promptParam
			}
		}

		window.addEventListener('popstate', handlePopState)
		return () => window.removeEventListener('popstate', handlePopState)
	}, [
		router.query,
		setSearchByAudio,
		setSearchByPrompt,
		clearAiSearch,
		lastKnownState,
	])
}

export const usePersistSearchScrollPosition =
	(): UsePersistSearchScrollPositionProps => {
		const [hasScrolledOnMount, setHasScrolledOnMount] = useState(false)

		const containerRef = useRef<HTMLDivElement>(null)

		const captureClick = (event: MouseEvent): void => {
			const target = event.target as HTMLElement

			/**
			 * We watch for any click in the results that navigates away from the page.
			 */
			if (target.tagName === 'A') {
				const mainContent = document.getElementById('main-content')
				const trackRow = target.closest('[id^="track-row-"]')

				if (mainContent && trackRow) {
					const scrollPosition = mainContent.scrollTop
					sessionStorage.setItem(
						'activeSearchScrollPosition',
						scrollPosition.toString()
					)
					sessionStorage.setItem('activeSearchTrackRow', trackRow.id)
				}
			}
		}

		const scrollToTrackRow = (container: HTMLElement): void => {
			const scrollPosition = sessionStorage.getItem(
				'activeSearchScrollPosition'
			)
			const trackRowId = sessionStorage.getItem('activeSearchTrackRow')
			const mainContent = document.getElementById('main-content')

			if (scrollPosition && trackRowId && mainContent) {
				/**
				 * We wait for the track row to be in the DOM before scrolling to it.
				 */
				const observer = new MutationObserver((_, obs) => {
					const targetElement = document.getElementById(trackRowId)
					if (targetElement) {
						mainContent.scrollTo(0, parseInt(scrollPosition))
						obs.disconnect()
					}
				})

				observer.observe(container, {
					childList: true,
					subtree: true,
				})
			}
		}

		const clearSearchScrollPosition = (): void => {
			sessionStorage.removeItem('activeSearchScrollPosition')
			sessionStorage.removeItem('activeSearchTrackRow')
		}

		useEffect(() => {
			if (!hasScrolledOnMount && containerRef.current) {
				scrollToTrackRow(containerRef.current)
				setHasScrolledOnMount(true)
			}
		}, [hasScrolledOnMount])

		useEffect(() => {
			const container = containerRef.current

			if (container) {
				container.addEventListener('click', captureClick)
			}

			return () => {
				if (container) {
					container.removeEventListener('click', captureClick)
				}
			}
		}, [hasScrolledOnMount])

		return {
			clearSearchScrollPosition,
			containerRef,
		}
	}
